import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '@/hooks/useEntitlements';
import { CheckCircle, RefreshCw } from 'lucide-react';

// Reached if a Systeme.io funnel's "thank you" page links back here after
// checkout. Purchases grant access via webhook (usually within a minute or
// two), not synchronously, so this just offers to re-check entitlements
// rather than polling a payment session like the old Stripe flow did.
const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { hasAccess, refresh, loading } = useEntitlements();
  const [checkedOnce, setCheckedOnce] = useState(false);

  useEffect(() => {
    refresh().then(() => setCheckedOnce(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPremium = hasAccess('apps_premium');

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#E0F7FA] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl border border-slate-100 text-center">
        {isPremium ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3">Thanks for your purchase!</h2>
            <p className="text-[#4A5568] mb-6">
              Your premium access is active.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg"
            >
              Go to dashboard
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-[#81B29A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <RefreshCw className={`w-8 h-8 text-[#81B29A] ${loading ? 'animate-spin' : ''}`} />
            </div>
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3">Thanks for your purchase!</h2>
            <p className="text-[#4A5568] mb-6">
              {checkedOnce
                ? "Your access hasn't shown up yet — it usually appears within a minute or two of completing checkout."
                : 'Checking your access...'}
            </p>
            <div className="space-y-3">
              <button
                onClick={refresh}
                disabled={loading}
                className="w-full bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'Check again'}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-white border-2 border-[#81B29A] text-[#81B29A] px-8 py-3 rounded-full font-semibold hover:bg-[#81B29A]/10 transition-all duration-300"
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
